import React from "react";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import CardMedia from "@mui/material/CardMedia";
import Tooltip from "@mui/material/Tooltip";
import DtvGrid from "@directv-digital/dds.ui.dtv-grid";
import { fusionScrollbarV2 } from "@directv-digital/dds.utils";
import DtvTheme, { colorPalette } from "@directv-digital/dds.ui.dtv-theme";
import { Channel } from "../../package-carousel.props";

export interface ChannelGridProps {
  channels?: Channel[];
}

const styles = {
  primaryColor: colorPalette.jet.main,
  channelGridContainer: {
    display: "flex",
    justifyContent: {
      xs: "center",
      sm: "flex-start"
    },
    alignItems: "flex-start",
    alignContent: "flex-start",
    gap: "16px 18px",
    p: { sm: "12px 0px" },
    mr: {
      xs: "18px",
      sm: "0px"
    },
    [DtvTheme.breakpoints.down(414)]: {
      gap: "16px 5px"
    },
    alignSelf: "stretch",
    flexWrap: "wrap",
    flexGrow: 1,
    overflowY: "auto",
    ...fusionScrollbarV2()
  },
  channelLogoContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    gap: "8px"
  },
  channelLogo: {
    display: "flex",
    width: {
      xs: "76px",
      sm: "80px"
    },
    height: {
      xs: "76px",
      sm: "80px"
    },
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "300px"
  },
  tooltipStyles: {
    opacity: "1",
    transform: "none",
    transition:
      "opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 133ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    transformOrigin: "left center",
    backgroundColor: "rgba(97, 97, 97, 0.92)",
    borderRadius: "4px",
    color: colorPalette.white.main,
    padding: "4px 8px",
    fontSize: "14px",
    maxWidth: "fit-content",
    margin: "0",
    wordWrap: "break-word",
    fontWeight: 400,
    fontFamily: "roboto-regular",
    position: "relative",
    marginLeft: "0px!important"
  },
  tooltipArrow: {
    position: "absolute",
    top: "0px",
    transform: "translate3d(0px, 8.8px, 0px)",
    left: "-10px",
    marginLeft: "-0.71em",
    height: "1em",
    width: "0.71em",
    overflow: "hidden",
    boxSizing: "border-box",
    color: "rgba(97, 97, 97, 0.9)",
    "::before": {
      transformOrigin: "100% 100%"
    }
  },
  tooltipModifiers: [
    {
      name: "preventOverflow",
      enabled: true
    },
    {
      name: "flip",
      enabled: true
    },
    {
      name: "offset",
      options: {
        offset: [0, -20]
      }
    }
  ]
};

const ChannelGrid = (props: ChannelGridProps) => {
  const { channels } = props;
  try {
    return (
      <DtvGrid
        sx={{ ...styles.channelGridContainer }}
      >
        {channels?.length > 0 &&
          channels?.map((channel, index) => (
              <DtvBox
                aria-label={`${channel?.channelNameLabel} icon`}
                data-testid="dtv-box"
                id={`icon-container-${channel?.channelNameLabel}`}
                key={index}
                tabIndex={-1}
                sx={{
                  ...styles.channelLogoContainer
                }}
              >
                <Tooltip
                  describeChild
                  enterTouchDelay={0}
                  key={index}
                  placement="bottom"
                  tabIndex={0}
                  title={channel?.channelNameLabel}
                  PopperProps={{
                    modifiers: styles.tooltipModifiers,
                    sx: { width: "unset !important" }
                  }}
                  componentsProps={{
                    tooltip: { sx: styles.tooltipStyles },
                    arrow: { sx: styles.tooltipArrow }
                  }}
                  sx={{
                    "& #popper[data-popper-escaped]": {
                      visibility: "hidden"
                    }
                  }}
                >
                  <CardMedia
                    alt={channel?.channelNameLabel || "channel logo icon"}
                    aria-labelledby={`icon-container-${channel?.channelNameLabel}`}
                    component={"img"}
                    key={index}
                    loading="lazy"
                    src={channel?.channelLogoPath}
                    sx={styles.channelLogo}
                  />
                </Tooltip>
              </DtvBox>
            ))}
      </DtvGrid>
    );
  } catch (error) {
    console.error(`PackageCarousel ChannelGrid error :: ${error}`);
    return <ErrorDisplay component="PackageCarousel ChannelGrid" data-testid="error-display" error={error} />;
  }
}

export default React.memo(ChannelGrid);
